import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { List, ListItem, Paper, Box, Typography, Button, Avatar } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// Question components removed - feature deprecated
import { prepareRoleColorForChat } from '../../utils/roleColorValidation';
import TypingIndicator from './TypingIndicator';
import { stripCtaMarkerStreaming } from '../../utils/ctaMarker';
import StreamActivityIndicator from './StreamActivityIndicator';
import MessageBubble from './MessageBubble';
import ProgressiveMarkdown from '../../streaming/ProgressiveMarkdown';
import { CHAT_MARKDOWN_TYPOGRAPHY } from './chatTypography';
import useRotatingStatus from '../../hooks/useRotatingStatus';
import useElapsedTime from '../../hooks/useElapsedTime';
import { getInitials, getColorFromString } from '../editor/features/roles/utils/rolColorUtils';

// ✅ Simple fallback typing indicator (if agentStatus not available)
const SimpleTypingIndicator = ({ theme }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 0.7,
      px: 2,
      py: 1.5,
      alignItems: 'center',
      minWidth: '60px',
    }}
  >
    {[0, 1, 2].map((dot) => (
      <Box
        key={dot}
        sx={{
          width: '5px',
          height: '5px',
          backgroundColor: theme.chat.typing.dotColor,
          borderRadius: '50%',
          animation: `dotPulse 1.4s ease-in-out infinite`,
          animationDelay: `${dot * 0.2}s`,
          '@keyframes dotPulse': {
            '0%, 80%, 100%': {
              opacity: 0.3,
              transform: 'scale(0.8)',
            },
            '40%': {
              opacity: 1,
              transform: 'scale(1.2)',
            },
          },
        }}
      />
    ))}
  </Box>
);

// Función para obtener el nombre del usuario desde el perfil de PostgreSQL
const getUserDisplayName = (userProfile, fallbackEmail) => {
  // 1. Preferir associated_person del perfil PostgreSQL
  if (userProfile?.associated_person) {
    return userProfile.associated_person;
  }

  // 2. Usar email del perfil PostgreSQL
  if (userProfile?.email) {
    return userProfile.email.split('@')[0];
  }

  // 3. Fallback: usar email de Supabase Auth
  if (fallbackEmail) {
    return fallbackEmail.split('@')[0];
  }

  // 4. Último recurso
  return 'Usuario';
};

// ── Isolated wrapper: owns useRotatingStatus and useElapsedTime so their
//    1s / 3-5s ticks don't re-render the entire message list ──
export const TypingIndicatorWrapper = React.memo(({ agentStatus, isTypingAnimating, theme }) => {
  const { t } = useTranslation();
  const displayTask = useRotatingStatus(agentStatus, t);

  // El reloj arranca al montar y se olvida al desmontar. Este wrapper vive
  // exactamente mientras `shouldShowTyping`, o sea un turno, así que cada turno
  // empieza en cero sin que nadie tenga que resetear nada a mano.
  //
  // Deliberadamente NO se calcula desde `agentStatus.lastUpdated`: ese campo se
  // refresca en cada `Update Status`, así que un turno que informa tres veces
  // mostraría 110 s de trabajo como tres tramos de 30 (#923).
  const { label: elapsedLabel } = useElapsedTime(true);

  return (agentStatus?.agentName && agentStatus.agentName !== '__processing__') ? (
    <TypingIndicator
      agentName={agentStatus.agentName}
      task={displayTask || ''}
      elapsedLabel={elapsedLabel}
      avatarColor={agentStatus.color || getColorFromString(agentStatus.agentName)}
      initials={getInitials(agentStatus.agentName)}
      theme={theme}
    />
  ) : (
    <TypingIndicator
      task={displayTask || 'Procesando...'}
      elapsedLabel={elapsedLabel}
      theme={theme}
    />
  );
});
TypingIndicatorWrapper.displayName = 'TypingIndicatorWrapper';

// Bubble width caps. En el chat a pantalla completa limitamos el ancho por
// legibilidad (líneas no demasiado largas). En `compact` (paneles estrechos del
// split screen del builder/preview) el contenedor ya restringe el ancho, así que
// ensanchamos las burbujas para no desperdiciar espacio. Solo afecta `md`+ — en
// móvil el panel se muestra a pantalla completa, igual que el chat.
const USER_BUBBLE_MAX_WIDTH = { xs: '85%', md: '60%' };
const USER_BUBBLE_MAX_WIDTH_COMPACT = { xs: '85%', md: '92%' };
const AGENT_BUBBLE_MAX_WIDTH = { xs: '92%', md: '80%' };
const AGENT_BUBBLE_MAX_WIDTH_COMPACT = { xs: '92%', md: '100%' };

// onAnswerQuestion prop removed - questions feature deprecated
const ChatMessages = ({ messages, isTyping, theme, messagesEndRef, isLoadingMessages, hasStoppedExecution, onRetry, isRetrying, userProfile, userEmail, agentStatus, threadId, highlightedMessageId, onFeedbackChange, onHandoffAction, isAnalyzingAttachments, streamingMessage, isStopping, scrollContainerRef, compact = false }) => {
  const colors = theme.chat;
  const { t } = useTranslation();

  // Constantes de módulo, no literales: son prop de una burbuja memoizada, y un
  // objeto nuevo por render anularía su bail-out (#930).
  const userBubbleMaxWidth = compact ? USER_BUBBLE_MAX_WIDTH_COMPACT : USER_BUBBLE_MAX_WIDTH;
  const agentBubbleMaxWidth = compact ? AGENT_BUBBLE_MAX_WIDTH_COMPACT : AGENT_BUBBLE_MAX_WIDTH;

  // Find the latest user message that carries attachments — that's the bubble
  // where the "Analizando documento..." chip should appear (Plan #53 Fase 1.2).
  const latestUserAttachmentIndex = useMemo(() => {
    if (!isAnalyzingAttachments) return -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.sender === 'usuario' && Array.isArray(m.attachments) && m.attachments.length > 0) {
        return i;
      }
    }
    return -1;
  }, [isAnalyzingAttachments, messages]);

  // ✅ Delayed unmount for smooth fade-out animation
  const [shouldShowTyping, setShouldShowTyping] = useState(false);
  const [isTypingAnimating, setIsTypingAnimating] = useState(false);

  // ✅ Scroll-to-bottom button visibility
  const [showScrollButton, setShowScrollButton] = useState(false);

  // El indicador de actividad avisa cuando entra y sale de silencio, para que el cursor
  // no siga prometiendo texto que no llega (ver `StreamActivityIndicator`). Es un
  // booleano, no un reloj: cambia dos veces por hueco, no una vez por segundo.
  const [enSilencio, setEnSilencio] = useState(false);

  // Sync isTyping prop with delayed state for fade-out animation
  useEffect(() => {
    if (isTyping) {
      // Show immediately with fade-in
      setShouldShowTyping(true);
      setIsTypingAnimating(true);
    } else {
      // Start fade-out animation
      setIsTypingAnimating(false);

      // Unmount after animation completes (250ms)
      const timer = setTimeout(() => {
        setShouldShowTyping(false);
      }, 250);

      return () => clearTimeout(timer);
    }
  }, [isTyping]);

  // ✅ Scroll detection for scroll-to-bottom button.
  // Escucha el contenedor scrollable REAL (el <Box overflow:auto> de Chat.jsx),
  // que llega por `scrollContainerRef`. Antes medía sobre `messagesEndRef.parentElement`
  // (el <ul>, que NO scrollea) → el evento nunca se disparaba y el botón no funcionaba.
  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    const handleScroll = () => {
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 100;

      setShowScrollButton(!isNearBottom);
    };

    handleScroll(); // estado inicial correcto al montar
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [scrollContainerRef]);

  // Un solo recorrido para los dos usos: eran dos `reduce` idénticos sobre el
  // mismo array. Los GATES siguen siendo distintos (uno depende de que haya una
  // ejecución parada, el otro de que no se esté escribiendo), así que siguen
  // siendo dos valores — lo que se unifica es el recorrido, no el criterio.
  const lastAgentMessageIndex = useMemo(
    () => messages.reduce((last, m, i) => (m.sender !== 'usuario' ? i : last), -1),
    [messages]
  );

  // Index of last agent message (for retry button placement)
  const lastAgentIndex = hasStoppedExecution ? lastAgentMessageIndex : -1;

  // Index of last completed agent message (for always-visible feedback actions)
  const lastCompletedAgentIndex = !isTyping ? lastAgentMessageIndex : -1;

  // ── #519 + #898: cuántos pasos del plan se completaron antes del ⏹ ─────────
  // El dato sale de `plan_execution`, NUNCA de los mensajes de progreso
  // suprimidos: G1 de #494 sigue intacta —ni uno se emite ni cambia de estado—,
  // y por eso el enlace lleva al detalle de la ejecución, que es donde vive el
  // registro autoritativo paso a paso.
  //
  // #898: la marca cuelga del MENSAJE que abrió el turno (`message.stoppedPlan`),
  // no de un estado efímero anclado a la cola del hilo. Dos consecuencias:
  //   - sobrevive al RETRY y al reload cuando hubo pasos ejecutados (el backend
  //     la sella en `metadata.stopped_plan`), que es cuando hubo efectos reales
  //     que el hilo no puede callarse;
  //   - no puede saltar sobre un mensaje que no es el suyo, que es lo que hacía
  //     el ancla posicional `messages.length - 1`.
  //
  // Sin `stepCount` (el Planner no siempre lo escribe) se informa solo de lo
  // completado; con 0 completados y sin total no hay nada que contar y no se
  // pinta nada — el pie se queda como estaba.
  const stoppedPlanLabelOf = (plan) => {
    if (!plan) return null;
    const done = plan.stepsCompleted ?? 0;
    const total = plan.stepCount;
    if (typeof total === 'number' && total > 0) {
      return t('chat.stoppedPlanSteps', '{{done}} de {{total}} pasos completados', { done, total });
    }
    if (done > 0) {
      return t('chat.stoppedPlanStepsNoTotal', '{{done}} pasos completados', { done });
    }
    return null;
  };

  // De qué mensaje SALE la marca y en cuál se PINTA.
  //
  // Sale del mensaje del usuario que abrió el turno — es el ancla que el backend
  // sella (`plan_execution.trigger_message_id`) y el único que existe siempre:
  // un turno cortado puede no tener ni una línea del agente.
  //
  // Se pinta ahí salvo que el turno dejara un parcial rotulado justo después:
  // ese parcial ya lleva su separador «⏹ Detenido» y es el último trozo visible
  // del turno, así que el contador se une a él en vez de abrir un segundo aviso
  // dos burbujas más arriba.
  // El pie se pinta por dos motivos distintos y solo uno admite RETRY: el corte
  // VIVO. Una marca histórica (la que sobrevive al reload) no puede ofrecer
  // reintentar un turno que el usuario ya dejó atrás — de ahí `showStopFooterRetry`
  // aparte de `showStopFooter`.
  //
  // El recorrido va memoizado por lo mismo que #930 sacó las burbujas a
  // `MessageBubble`: este componente se re-renderiza en CADA token de streaming, y
  // un `findIndex` sobre el hilo entero por token es justo el coste que aquel
  // refactor vino a quitar. `t` es estable salvo cambio de idioma.
  const { planMark, planMarkLabel, planMarkIndex } = useMemo(() => {
    const sourceIndex = messages.findIndex((m) => stoppedPlanLabelOf(m.stoppedPlan));
    if (sourceIndex < 0) return { planMark: null, planMarkLabel: null, planMarkIndex: -1 };

    const mark = messages[sourceIndex].stoppedPlan;
    const after = messages[sourceIndex + 1];
    const painted = (
      after
      && after.sender !== 'usuario'
      && after.cancelledByUser === true
    ) ? sourceIndex + 1 : sourceIndex;

    return { planMark: mark, planMarkLabel: stoppedPlanLabelOf(mark), planMarkIndex: painted };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, t]);

  // Solo el texto: el `<Link>` lo construye MessageBubble. Pasar una función
  // que lo renderice sería una prop nueva en cada render y mataría el memo.
  const executionDetailLabel = t('chat.stoppedPlanDetail', 'Ver detalle');

  return (<List role="log" aria-live="polite" aria-relevant="additions" sx={{ p: 0 }}>
    {messages.map((message, index) => {
      const isUser = message.sender === 'usuario';
      return (
        <MessageBubble
          key={message.id || index}
          message={message}
          theme={theme}
          colors={colors}
          bubbleMaxWidth={isUser ? userBubbleMaxWidth : agentBubbleMaxWidth}
          isHighlighted={message.id === highlightedMessageId}
          isAnalyzingAttachment={index === latestUserAttachmentIndex}
          threadId={threadId}
          isStoppedPlanAnchor={index === planMarkIndex}
          stoppedPlanLabel={planMarkLabel || undefined}
          stoppedPlanExecutionId={planMark?.executionId}
          executionDetailLabel={executionDetailLabel}
          showStopFooter={
            (
              (hasStoppedExecution && index === messages.length - 1)
              || index === planMarkIndex
            )
            && !(!isUser && message.cancelledByUser === true)
          }
          showStopFooterRetry={hasStoppedExecution && index === messages.length - 1}
          showRetry={!isTyping && index === lastAgentIndex}
          isLastMessage={index === lastCompletedAgentIndex}
          previousUserMessage={
            index > 0 && messages[index - 1]?.sender === 'usuario'
              ? messages[index - 1].content : undefined
          }
          onRetry={onRetry}
          isRetrying={isRetrying}
          onFeedbackChange={onFeedbackChange}
          onHandoffAction={onHandoffAction}
        />
      );
    })}
    {/* ── Streaming live bubble (PRP-B): ghost text + tool loader + progressive markdown ── */}
    {streamingMessage && (
      streamingMessage.text ||
      streamingMessage.preToolText ||
      (streamingMessage.toolCalls && streamingMessage.toolCalls.length > 0)
    ) && (() => {
      const seedColor = streamingMessage.color
        || (streamingMessage.agentName ? getColorFromString(streamingMessage.agentName) : null);
      const roleColors = prepareRoleColorForChat(seedColor, theme);
      // Live mid-tool status from the agent_status_updated side-channel — the
      // only progress signal inside an Agent V2 loop, where the fork's
      // tool-call-start never reaches the callback (see Chat.jsx onDelta).
      const liveStatusText = agentStatus?.currentTask || null;
      return (
        <ListItem sx={{
          justifyContent: 'flex-start',
          px: { xs: 2.5, md: 2 },
          py: 0,
          mb: { xs: 0.5, md: 1 },
          gap: { xs: 0.75, md: 1 },
          alignItems: 'flex-start',
        }}>
          <Avatar sx={{
            background: roleColors.avatarBgGradient,
            boxShadow: roleColors.avatarShadow,
            color: roleColors.avatarText,
            width: { xs: 24, md: 32 },
            height: { xs: 24, md: 32 },
            flexShrink: 0,
            fontWeight: 600,
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            mt: 0.5,
          }}>
            {getInitials(streamingMessage.agentName || 'A')}
          </Avatar>
          <Box sx={{ flexGrow: 1, maxWidth: agentBubbleMaxWidth, minWidth: 0 }}>
            {streamingMessage.agentName && (
              <Box sx={{ display: 'flex', mb: 0.25, ml: 0.5 }}>
                <Typography variant="caption" sx={{
                  fontWeight: 600,
                  color: roleColors.nameText,
                  fontSize: { xs: '0.7rem', md: '0.75rem' },
                }}>
                  {streamingMessage.agentName}
                </Typography>
              </Box>
            )}
            <Paper elevation={0} sx={{
              px: { xs: 1.5, md: 2 },
              py: { xs: 1, md: 1.25 },
              borderRadius: '3px 12px 12px 12px',
              bgcolor: colors.messages.agent.background,
              color: colors.messages.agent.text,
              backgroundImage: 'none',
              border: `1px solid ${colors.messages.agent.borderColor}`,
            }}>
              {/* Ghost text accumulated from previous rounds */}
              {streamingMessage.preToolText && (
                <Box sx={{
                  opacity: 0.65,
                  mb: 1,
                  // #1116-A: la misma tipografía que la burbuja persistida, de
                  // una sola fuente. Ver `chatTypography.js`.
                  ...CHAT_MARKDOWN_TYPOGRAPHY,
                  '& p:first-of-type': { marginTop: 0 },
                  '& p:last-child': { marginBottom: 0 },
                }}>
                  <ProgressiveMarkdown text={stripCtaMarkerStreaming(streamingMessage.preToolText)} />
                </Box>
              )}

              {/* Live text of the current round with blinking cursor */}
              {streamingMessage.text && (
                <Box sx={{
                  position: 'relative',
                  ...CHAT_MARKDOWN_TYPOGRAPHY,
                  '& p:first-of-type': { marginTop: 0 },
                  '& p:last-child': { marginBottom: 0 },
                }}>
                  <ProgressiveMarkdown text={stripCtaMarkerStreaming(streamingMessage.text)} />
                  {/* Cursor visible sólo mientras el round aún produce tokens.
                      Cuando llega delta done:true, roundDone=true → cursor off
                      al instante (evita el blink durante los ~700-1000ms entre
                      el último delta y el agent_message persistido).

                      Y off también durante el silencio: ahí la ronda sigue abierta
                      —el modelo está escribiendo los argumentos de una herramienta—
                      así que `roundDone` es false y el cursor se quedaría parpadeando
                      hasta 37 s prometiendo una letra que no viene. El indicador del
                      pie ya dice lo que pasa, y dice lo contrario. */}
                  {!streamingMessage.roundDone && !enSilencio && (
                    <Box component="span"
                      data-testid="stream-cursor"
                      // Decorativo: no aporta nada a quien no ve la pantalla, y un
                      // lector lo anunciaría dentro de la burbuja. Mismo criterio que
                      // el cronómetro de `StreamingToolLoader`.
                      aria-hidden="true"
                      sx={{
                      display: 'inline-block',
                      width: '2px',
                      height: '1em',
                      bgcolor: 'text.primary',
                      ml: '1px',
                      verticalAlign: 'text-bottom',
                      '@keyframes streamCursor': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0 },
                      },
                      animation: 'streamCursor 1s step-end infinite',
                      '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none',
                        opacity: 0.5,
                      },
                    }} />
                  )}
                </Box>
              )}

              {/* Indicador de actividad, al PIE de la burbuja para que el texto ya
                  escrito quede encima.

                  ⚠️ Va SIEMPRE montado, nunca como `{condición && …}`: dentro late el
                  cronómetro, que mide el TURNO y no cada aparición. Un turno de Agent V2
                  son varias vueltas tool→texto→tool, y el indicador se apaga en cuanto
                  llega el texto de cada una; con montaje condicional el reloj arrancaba
                  de cero cada vez y un turno de dos minutos se veía como «3s… 4s… 2s…»
                  — o no se veía, porque ninguna vuelta llegaba al umbral (#923).

                  Qué se muestra lo decide `decidirActividad` (`streaming/activityPolicy.js`),
                  compartida con el chat modular y probada aparte. */}
              <StreamActivityIndicator
                streamingMessage={streamingMessage}
                liveStatusText={liveStatusText}
                isStopping={isStopping}
                onSilencioChange={setEnSilencio}
              />
            </Paper>
          </Box>
        </ListItem>
      );
    })()}

    {shouldShowTyping && (
      <ListItem sx={{
        justifyContent: 'flex-start',
        px: { xs: 2.5, md: 2 }, // Match message list item padding
        py: 0,
        mb: { xs: 0.5, md: 1 },
        // ✅ Smooth fade-out animation (250ms)
        transition: 'opacity 0.25s ease-out, transform 0.25s ease-out',
        opacity: isTypingAnimating ? 1 : 0,
        transform: isTypingAnimating ? 'translateY(0)' : 'translateY(-10px)',
        willChange: 'opacity, transform', // Optimize for mobile
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none', // Respect accessibility preferences
        }
      }}>
        <TypingIndicatorWrapper
          agentStatus={agentStatus}
          isTypingAnimating={isTypingAnimating}
          theme={theme}
        />
      </ListItem>
    )}

    {/* Scroll-to-bottom button */}
    {showScrollButton && (
      <Box
        sx={{
          position: 'sticky',
          bottom: 'calc(16px + env(safe-area-inset-bottom))',
          // OJO: nada de `left: 50%` + `translateX(-50%)` aquí. Ese patrón es de
          // position:absolute/fixed; con sticky el Box sigue en flujo y ocupa el
          // 100% del ancho, así que el translate arrastraba la franja entera medio
          // ancho a la izquierda y recortaba el botón contra el borde.
          // El centrado ya lo hace el justifyContent de abajo.
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none'
        }}
      >
        <Button
          variant="contained"
          size="small"
          onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
          endIcon={<KeyboardArrowDownIcon />}
          aria-label={t('chat.scrollToBottom', 'Ir al final')}
          sx={{
            borderRadius: 3,
            px: 2,
            py: 0.75,
            textTransform: 'none',
            boxShadow: 3,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            pointerEvents: 'auto',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: 4,
              bgcolor: 'primary.dark',
              transform: 'translateY(-2px)'
            },
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none'
            }
          }}
        >
          {/* No dice "mensajes nuevos": showScrollButton solo mide distancia al
              fondo, así que también aparece releyendo una conversación parada. */}
          {t('chat.scrollToBottom', 'Ir al final')}
        </Button>
      </Box>
    )}

    <div ref={messagesEndRef} />
  </List>
  );
};

ChatMessages.propTypes = {
  messages: PropTypes.array.isRequired,
  isTyping: PropTypes.bool,
  theme: PropTypes.object.isRequired,
  messagesEndRef: PropTypes.object,
  onAnswerQuestion: PropTypes.func,
  isLoadingMessages: PropTypes.bool,
  hasStoppedExecution: PropTypes.bool,
  // #898: el progreso del plan cortado ya no es un prop del componente — viaja
  // en el mensaje que abrió el turno (`message.stoppedPlan`), que es quien lo
  // conserva tras un RETRY o un reload.
  onRetry: PropTypes.func,
  isRetrying: PropTypes.bool,
  userProfile: PropTypes.object,
  // Los nombres son los del canal `agent_status_updated`, no una versión abreviada:
  // el código lee `agentName` y `currentTask`. Este shape declaró `{name, task}`
  // hasta el 2026-09-07 — nunca falló, porque PropTypes ignora lo que no declaras,
  // pero describía un contrato que no existe.
  agentStatus: PropTypes.shape({
    agentName: PropTypes.string,
    currentTask: PropTypes.string,
    color: PropTypes.string,
    lastUpdated: PropTypes.string
  }),
  threadId: PropTypes.string,
  // Mensaje al que Chat.jsx acaba de llevar el scroll (deep-link de ejecución, #212).
  highlightedMessageId: PropTypes.string,
  onFeedbackChange: PropTypes.func,
  onHandoffAction: PropTypes.func,
  isAnalyzingAttachments: PropTypes.bool,
  streamingMessage: PropTypes.shape({
    text: PropTypes.string,
    preToolText: PropTypes.string,
    toolCalls: PropTypes.array,
    agentName: PropTypes.string,
    color: PropTypes.string,
    roundDone: PropTypes.bool,
  }),
  // El usuario pulsó ⏹ y aún no llegó la confirmación del backend: apaga el
  // indicador de actividad, que si no diría «trabajando» justo cuando el usuario
  // cree haber parado.
  isStopping: PropTypes.bool,
  // Ref al contenedor scrollable real (Box overflow:auto de Chat.jsx) para el
  // botón "scroll-to-bottom". Opcional: otros consumidores (Onboarding, Builder)
  // no lo pasan y el botón simplemente no se muestra.
  scrollContainerRef: PropTypes.shape({ current: PropTypes.any }),
  // Ensancha las burbujas para paneles estrechos (split screen builder/preview).
  compact: PropTypes.bool,
};

export default ChatMessages; 